import { XMLParser } from "fast-xml-parser";
import * as Sentry from "@sentry/react-native";

export type IdeAddress = {
    postalCode: string;
    city: string;
    street: string;
    houseNumber: string;
    cantonAbbreviation: string;
    country: string;
};
export type IdeCompany = { name: string | null; uid: string | null; address: IdeAddress | null };

class IdeCompanySearchClass {
    private baseUrl: string;
    private xmlParser: XMLParser;

    /**
     * Creates an instance of IdeCompanySearch.
     * @param {string} baseUrl - The base URL of the PublicServices SOAP endpoint.
     */
    constructor(baseUrl = "https://www.uid-wse.admin.ch/V5.0/PublicServices.svc") {
        this.baseUrl = baseUrl;
        this.xmlParser = new XMLParser({
            ignoreAttributes: false,
            trimValues: true,
        });
    }

    /**
     * Searches for companies by name using the public webservice.
     *
     * @param {string} companyName - The name or part of the name of the company to search for.
     * @returns {Promise<IdeCompany[]>} A promise that resolves to an array of company objects.
     */
    async searchCompaniesByName(companyName: string): Promise<IdeCompany[]> {
        try {
            const soapRequestBody = `
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:uid="http://www.uid.admin.ch/xmlns/uid-wse" xmlns:ns="http://www.uid.admin.ch/xmlns/uid-wse/5" xmlns:ns1="http://www.ech.ch/xmlns/eCH-0097/5" xmlns:ns2="http://www.uid.admin.ch/xmlns/uid-wse-shared/2">
    <soapenv:Header/>
    <soapenv:Body>
        <uid:Search>
             <!--Optional:-->
             <uid:searchParameters>
                <ns:uidEntitySearchParameters>
                   <!--Optional:-->
                   <ns:organisationName>${companyName}%</ns:organisationName>
                </ns:uidEntitySearchParameters>
             </uid:searchParameters>
             <!--Optional:-->
             <uid:config>
                <ns2:searchMode>Normal</ns2:searchMode>
                <ns2:maxNumberOfRecords>20</ns2:maxNumberOfRecords>
                <ns2:searchNameAndAddressHistory>0</ns2:searchNameAndAddressHistory>
             </uid:config>
        </uid:Search>
    </soapenv:Body>
</soapenv:Envelope>
`;
            const response = await fetch(this.baseUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "text/xml; charset=utf-8",
                    SOAPAction: '"http://www.uid.admin.ch/xmlns/uid-wse/IPublicServices/Search"',
                },
                body: soapRequestBody,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
            }

            const responseText = await response.text();

            const parsedXml = this.xmlParser.parse(responseText);

            const fault =
                parsedXml?.["s:Envelope"]?.["s:Body"]?.["s:Fault"] ||
                parsedXml?.Envelope?.Body?.Fault;

            if (fault) {
                const faultcode = fault.faultcode || fault.Faultcode || "Unknown Fault Code";
                const faultstring = fault.faultstring || fault.Faultstring || "Unknown SOAP Fault";
                const errorDetail =
                    fault.detail?.businessFault?.errorDetail ||
                    fault.Detail?.BusinessFault?.ErrorDetail ||
                    "";
                throw new Error(
                    `SOAP Fault [${faultcode}]: ${faultstring} - Detail: ${errorDetail}`,
                );
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let resultItems: any[] = [];
            const searchResult =
                parsedXml?.["s:Envelope"]?.["s:Body"]?.["SearchResponse"]?.["SearchResult"]?.[
                    "uidEntitySearchResultItem"
                ] ||
                parsedXml?.Envelope?.Body?.SearchResponse?.SearchResult?.uidEntitySearchResultItem;

            if (searchResult) {
                // Ensure resultItems is always an array
                resultItems = Array.isArray(searchResult) ? searchResult : [searchResult];
            }

            const companies: IdeCompany[] = [];

            resultItems.forEach((item) => {
                const name =
                    item["organisation"]?.["organisation"]?.["organisationIdentification"]?.[
                        "organisationName"
                    ]?.["#text"] || "N/A";
                const uid =
                    item["organisation"]?.["organisation"]?.["organisationIdentification"]?.[
                        "uid"
                    ]?.["uidOrganisationId"] || "N/A";
                const uidCat =
                    item["organisation"]?.["organisation"]?.["organisationIdentification"]?.[
                        "uid"
                    ]?.["uidOrganisationIdCategorie"] || "N/A";

                let address: IdeAddress | null = null;
                const addressData = item["organisation"]?.["organisation"]?.["address"] || "N/A";

                if (addressData) {
                    const street = addressData["eCH-0010:street"] || addressData.street || "";
                    const houseNumber: number =
                        addressData["eCH-0010:houseNumber"] || addressData.houseNumber || "";
                    const postalCode: number =
                        addressData["eCH-0010:swissZipCode"] || addressData.swissZipCode || "";
                    const city = addressData["eCH-0010:town"] || addressData.town || "";
                    const cantonAbbreviation =
                        addressData["eCH-0010:cantonAbbreviation"] ||
                        addressData.cantonAbbreviation ||
                        "";
                    const country =
                        addressData["eCH-0010:countryIdISO2"] || addressData.countryIdISO2 || "";
                    address = {
                        street,
                        houseNumber: houseNumber.toString(),
                        postalCode: postalCode.toString(),
                        city,
                        cantonAbbreviation,
                        country,
                    };
                }

                if (name !== "N/A" && uid !== "N/A" && uidCat !== "N/A") {
                    companies.push({
                        name: name,
                        uid: `${uidCat}${uid}`,
                        address: address,
                    });
                }
            });

            return companies;
        } catch (error) {
            Sentry.captureException(error);
            return []; // Return empty array on error
        }
    }
}

export const IdeCompanySearch = IdeCompanySearchClass;
